import { useLayoutEffect, useState } from 'react';

const checkVisibility = (el: Element | undefined) => {
  if (!el) return false;

  const bounds = el.getBoundingClientRect();
  const documentElementClientHeight = document.documentElement
    ? document.documentElement.clientHeight
    : 0;
  const documentElementClientWidth = document.documentElement
    ? document.documentElement.clientWidth
    : 0;
  const preload = 10; // Offset to load before entering viewport set in px

  return (
    bounds.top >= -1 * (bounds.height / 2) &&
    bounds.left >= -1 * (bounds.width / 2) &&
    bounds.bottom <=
      (window.innerHeight || documentElementClientHeight) + bounds.height + preload &&
    bounds.right <= (window.innerWidth || documentElementClientWidth) + bounds.width + preload
  );
};

/*
    forwardedElement is optional. If provided, scroll eventlistner will be attach to the element.
    Useful when a extra listner for x-axis scroll is needed for i.e. a gallery.

    visibleKey is optional. If provided, the init event will be triggered once the 'key' changes.
    Useful when a gallery is using next and prev-buttons to trigger next view.
*/

interface Optionals {
  forwardedElement: Element | undefined;
  visibleKey: number | undefined;
}

const validVisibleKey = (optionals?: Optionals): boolean => {
  if (!optionals) return false;

  const { visibleKey } = optionals;
  if (!visibleKey) return false;
  return true;
};

export const useDelayedRender = (
  callback: () => void,
  element: Element | null,
  optionals?: Optionals,
) => {
  if (process.env.SSR) return;
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const handler = () => {
    if (element) {
      const visible = checkVisibility(element);
      if (visible) {
        callback();
        unregister();
      }
    }
  };

  const unregister = () => {
    setHasBeenVisible(true);
    window.removeEventListener('scroll', handler);
    if (optionals && optionals.forwardedElement)
      optionals.forwardedElement.removeEventListener('scroll', handler);
  };

  useLayoutEffect(() => {
    if (!element) return;

    window.addEventListener('scroll', handler);
    if (optionals && optionals.forwardedElement)
      optionals.forwardedElement.addEventListener('scroll', handler);
    return unregister;
  }, [element]);

  useLayoutEffect(() => {
    if (!element || hasBeenVisible || !validVisibleKey(optionals)) return;

    const timeout = setTimeout(() => {
      const visible = checkVisibility(element);
      if (visible && !hasBeenVisible) {
        callback();
        unregister();
      }
    }, 250);

    return (): void => clearTimeout(timeout);
  }, [optionals]);

  useLayoutEffect(() => {
    if (!element) return;

    const timeout = setTimeout(() => {
      const visible = checkVisibility(element);
      if (visible && !hasBeenVisible) {
        callback();
        unregister();
      }
    }, 250);
    return (): void => clearTimeout(timeout);
  }, [element]);
};
